import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowLeft, Calendar, Tag, User, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { blogPosts } from "@/data/blogData";
import type { BlogPost as BlogPostType, BlogSection } from "@/data/blogData";
import { toast } from "@/components/ui/use-toast";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPostType | undefined>(undefined);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Find post in current language or fallback
    let foundPost = blogPosts[language].find(p => p.slug === slug);
    let postLang = language;
    if (!foundPost) {
      const otherLang = language === 'en' ? 'es' : 'en';
      foundPost = blogPosts[otherLang].find(p => p.slug === slug);
      postLang = otherLang;
    }
    if (foundPost) {
      if (postLang !== language) setLanguage(postLang);
      setPost(foundPost);
      document.title = `${foundPost.title} | Inbloqs`;
    } else {
      navigate('/blog');
    }
  }, [slug, language, navigate, setLanguage]);

  // Share the article or copy link
  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: post!.title, text: post!.excerpt, url }).catch(console.error);
    } else {
      navigator.clipboard.writeText(url)
        .then(() => {
          toast({ title: language === "en" ? "Link copied to clipboard" : "Enlace copiado al portapapeles" });
        })
        .catch(console.error);
    }
  };

  if (!post) return null;

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow pt-28 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <Button asChild variant="ghost" className="mb-6 rounded-full hover:bg-slate-100 dark:hover:bg-slate-900">
            <Link to="/blog">
              <ArrowLeft className="mr-2 w-4 h-4" />
              {language === "en" ? "Back to Blog" : "Volver al Blog"}
            </Link>
          </Button>
          
          <div className={`h-64 ${post.imageColor} rounded-3xl mb-8 flex items-center justify-center border border-slate-200/70 dark:border-slate-800/70 shadow-[0_16px_45px_-24px_rgba(15,23,42,0.3)]`}>
            <div className="w-24 h-24 rounded-full bg-white/20 dark:bg-black/20"></div>
          </div>
          
          <div className="flex flex-wrap gap-4 mb-6">
            <span className="px-3 py-1 rounded-full bg-blue-100/80 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 text-sm font-medium flex items-center">
              <Tag className="w-4 h-4 mr-1" /> {post.category}
            </span>
            <span className="px-3 py-1 rounded-full bg-slate-100/80 dark:bg-slate-900 text-slate-700 dark:text-slate-300 text-sm font-medium flex items-center">
              <Calendar className="w-4 h-4 mr-1" /> {post.date}
            </span>
            <span className="px-3 py-1 rounded-full bg-emerald-100/80 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 text-sm font-medium flex items-center">
              <User className="w-4 h-4 mr-1" /> {language === "en" ? "Inbloqs Team" : "Equipo Inbloqs"}
            </span>
          </div>
          
          <h1 className="section-heading mb-6 text-left">{post.title}</h1>
          
          <div className="prose prose-slate dark:prose-invert max-w-none prose-headings:text-slate-900 dark:prose-headings:text-white prose-p:text-slate-700 dark:prose-p:text-slate-300">
            <p className="text-xl mb-8 text-slate-700 dark:text-slate-300">{post.excerpt}</p>
            
            {post.content.map((paragraph: string, idx: number) => (
              <p key={idx} className="mb-4">{paragraph}</p>
            ))}

            {post.sections.map((section: BlogSection, idx: number) => (
              <div key={idx} className="my-8">
                <h2 className="text-2xl font-bold mb-4">{section.title}</h2>
                {section.paragraphs.map((paragraph: string, pidx: number) => (
                  <p key={pidx} className="mb-4">{paragraph}</p>
                ))}
              </div>
            ))}

            <div className="mt-12 border-t border-slate-200 dark:border-slate-800 pt-6">
              <Button variant="outline" size="sm" className="flex items-center rounded-full border border-slate-200 bg-white/80 text-slate-800 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-200 dark:hover:bg-slate-900" onClick={handleShare}>
                <Share2 className="mr-2 w-4 h-4" />
                {language === "en" ? "Share this article" : "Compartir este artículo"}
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
